import TaskQueue from 'app/utils/queues/TaskQueue';
import { IValueTransformTask, IValueTransformMiddleware, IAsyncState, IState } from './constants';
import SubscribableState from 'app/utils/SubscribableState';
import { createExternallyControlledPromise } from 'app/utils/async';


type OnError = (error: unknown) => void;

export interface IAsyncStateOptions<T> {
  /** Initial value */
  init: T;
  /**
   * Called whenever a task rejects or throws
   */
  onError?: OnError;
  /**
   * Optional middleware, takes `(state, action)` arguments, is
   * supposed to call `action(state)` at some point to produce
   * next `state` value and return it.
   */
  middleware?: IValueTransformMiddleware<T>;
  onListenerError?: OnError;
}

export default class AsyncState<T> implements IAsyncState<T> {
  private queue = new TaskQueue();
  private lastSend: Promise<void> = Promise.resolve();

  private middleware: IValueTransformMiddleware<T>;
  private onError: OnError;
  private store: SubscribableState<IState<T>>;

  public constructor({
    init,
    onError = (error) => { throw error; },
    onListenerError,
    middleware = (state: T, task: IValueTransformTask<T>, cancelToken) => task(state, cancelToken),
  }: IAsyncStateOptions<T>) {
    this.onError = onError;
    this.middleware = middleware;

    this.store = new SubscribableState<IState<T>>(
      {
        value: init,
        isBusy: false,
      },
      {
        onListenerError,
      },
    );
  }

  public scheduleUpdate(task: IValueTransformTask<T>) {
    if (!this.isBusy()) {
      this.store.setState({ isBusy: true });
    }

    const currentSend = this.queue.push(async (cancelToken) => {
      const { value } = this.store.state;
      let next = value;

      try {
        next = await this.middleware(value, task, cancelToken);
      } catch (error) {
        if (error !== cancelToken.getReason()) {
          this.onError(error);
        }
      }

      const anotherTaskScheduled = currentSend !== this.lastSend;

      if (next !== value) {
        this.store.setState({
          value: next,
          isBusy: anotherTaskScheduled,
        });
      } else if (!anotherTaskScheduled) {
        this.store.setState({
          value,
          isBusy: false,
        });
      } else {
        // If the task didn't change the value and there's another
        // task already scheduled, there's nothing to change in
        // the state.
      }
    });

    this.lastSend = currentSend;
  }

  public abort() {
    return this.queue.abort();
  }

  public getState() {
    return this.store.state;
  }

  public getValue() {
    return this.store.state.value;
  }

  public isBusy() {
    return this.store.state.isBusy;
  }

  public subscribe(callback: () => void) {
    return this.store.subscribe(callback);
  }

  public wait() {
    const { promise, resolve } = createExternallyControlledPromise<void>();

    this.queue.push(() => resolve());

    return promise;
  }
}
